import { Grid, Text, Table, useMantineTheme, Collapse } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ScrollArea, ToggleCollapse } from "@tfg/ui"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityRequestItem } from "~/pages/SupplierPortal/TraceabilityRequests/components/TraceabilityRequestItem.tsx"
import { InformationRequestContextProvider } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import {
  assignDuplicateProductionSites,
  RequestWithOpItemFromOperationTree,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context"

import { InformationRequestSummary } from "./InformationRequestSummary"
import { buildRows, getSortedRanks } from "./traceability-request-rows.builder"

interface TraceabilityRequestProps {
  request: RequestWithOpItemFromOperationTree
  index: number
  setIrsToSubmit: React.Dispatch<React.SetStateAction<Set<number>>>
}

export const TraceabilityRequest = React.memo(
  ({ request, index, setIrsToSubmit }: TraceabilityRequestProps) => {
    const { isAdmin } = useAuth()
    const [isOpen, { toggle }] = useDisclosure(index === 0)
    const { t } = useTranslation("traceability")
    const { colors } = useMantineTheme()

    const operationItems = assignDuplicateProductionSites(request.opItems)

    const ranks = useMemo(
      () => getSortedRanks(operationItems),
      [operationItems]
    )
    const rows = useMemo(
      () => buildRows(operationItems, ranks),
      [operationItems, ranks]
    )

    const requestItems = useMemo(
      () =>
        rows.map((row, index) => (
          <TraceabilityRequestItem
            key={`${request.original.id}-row-${index}`}
            index={index}
            operationsItemWithRowspan={row}
          />
        )),
      [rows, request]
    )

    return (
      <InformationRequestContextProvider request={request}>
        <InformationRequestSummary setIrsToSubmit={setIrsToSubmit} />
        <ToggleCollapse
          open={isOpen}
          toggle={toggle}
          seeMoreLabel={t("request.seeMore")}
          seeLessLabel={t("request.seeLess")}
        />
        <Collapse in={isOpen}>
          <Grid style={{ border: "1px solid", borderColor: colors.gray[3] }}>
            <Grid.Col span={12} bg="white">
              <ScrollArea
                showEdgeFade
                type="auto"
                w="100%"
                h="60vh"
                zoomable
                hideScrollbar
              >
                <Table
                  style={{
                    borderCollapse: "collapse",
                    border: "none",
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      {ranks.map((rank) => (
                        <Table.Td key={`${request.original.id}-${rank}`} />
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {request.error && isAdmin && (
                      <Table.Tr>
                        <Table.Td>
                          <Text c="violet">{request.error}</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {requestItems}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Grid.Col>
          </Grid>
        </Collapse>
      </InformationRequestContextProvider>
    )
  }
)
